import { Car, LayoutDashboard, MapPin, Search, ShieldAlert, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusDot } from '@/components/common/status-badge';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { useFleet } from '@/hooks/use-fleet';
import { speed } from '@/lib/format';
import { useUiStore } from '@/stores/ui-store';

/**
 * ⌘K over the live fleet. Searching a plate here jumps the map rather than
 * opening a results page — the map is where an operator wants to end up.
 */
export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const { vehicles } = useFleet();
  const selectVehicle = useUiStore((s) => s.selectVehicle);
  const setDrawerOpen = useUiStore((s) => s.setDrawerOpen);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const jumpToVehicle = (vehicleId: string) => {
    setOpen(false);
    setDrawerOpen(true);
    selectVehicle(vehicleId, { fly: true });
    navigate('/map');
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group flex h-8 w-full max-w-[300px] items-center gap-2 rounded-sm border bg-background px-2.5 text-left text-xs text-muted-foreground transition-colors hover:border-ring/40 hover:text-foreground"
      >
        <Search className="size-3.5 shrink-0" />
        <span className="flex-1 truncate">Search plate, driver, place…</span>
        <kbd className="pointer-events-none hidden shrink-0 rounded-[3px] border bg-muted px-1 tnum text-[10px] font-medium sm:inline-block">
          ⌘K
        </kbd>
      </button>

      <CommandDialog open={open} onOpenChange={setOpen} title="Search" description="Find a vehicle or jump to a screen">
        <CommandInput placeholder="Plate number, make, group…" />
        <CommandList>
          <CommandEmpty>No match in this fleet.</CommandEmpty>

          <CommandGroup heading="Vehicles">
            {vehicles.slice(0, 40).map((entry) => (
              <CommandItem
                key={entry.vehicle.id}
                value={`${entry.vehicle.plateNumber} ${entry.vehicle.make} ${entry.vehicle.model} ${entry.vehicle.group ?? ''}`}
                onSelect={() => jumpToVehicle(entry.vehicle.id)}
                className="gap-2.5"
              >
                <StatusDot status={entry.status} size={7} />
                <span className="tnum text-xs font-medium">{entry.vehicle.plateNumber}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {entry.vehicle.make} {entry.vehicle.model}
                </span>
                <span className="tnum ml-auto text-[11px] text-muted-foreground">
                  {speed(entry.speedKph)}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandSeparator />

          <CommandGroup heading="Go to">
            <CommandItem onSelect={() => (setOpen(false), navigate('/dashboard'))}>
              <LayoutDashboard className="size-4" /> Dashboard
            </CommandItem>
            <CommandItem onSelect={() => (setOpen(false), navigate('/map'))}>
              <MapPin className="size-4" /> Live map
            </CommandItem>
            <CommandItem onSelect={() => (setOpen(false), navigate('/alerts'))}>
              <ShieldAlert className="size-4" /> Alerts
            </CommandItem>
            <CommandItem onSelect={() => (setOpen(false), navigate('/geofences'))}>
              <MapPin className="size-4" /> Geofences
            </CommandItem>
            <CommandItem onSelect={() => (setOpen(false), navigate('/admin/vehicles'))}>
              <Car className="size-4" /> Manage vehicles
            </CommandItem>
            <CommandItem onSelect={() => (setOpen(false), navigate('/admin/users'))}>
              <Users className="size-4" /> Manage users
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
